import React from 'react';
import ReactDOM from 'react-dom';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * Apply / Cancel pill pinned to the bottom of the viewport during
 * refine mode. Portaled directly to `<body>` (NOT rendered inside
 * the annotation stage) — the stage uses `overflow: hidden` to
 * clip mask overlays, which would also clip a bottom-docked bar.
 *
 * PRD §8.9 non-negotiable. Adaptive label per §8.8:
 *   - pure bbox drag → "✓ Save my box"  (use_box_as_mask: true)
 *   - points present  → "✨ Smart refit (SAM3)"
 *
 * Chunk 2 compressed-forward scope: no points yet (that ships in
 * Chunk 3+). We always show the "Save my box" label.
 *
 * Web-only render. Native falls through to a no-op because the
 * whole segmentation flow is gated to web.
 */

interface FloatingActionBarProps {
  visible: boolean;
  applyLabel?: string;
  cancelLabel?: string;
  onApply: () => void;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  visible,
  applyLabel = '✓ Save my box',
  cancelLabel = 'Cancel',
  onApply,
  onCancel,
  loading = false,
  disabled = false,
}) => {
  if (Platform.OS !== 'web' || !visible) return null;
  const bar = (
    <View style={styles.bar}>
      <Pressable
        onPress={onCancel}
        disabled={loading}
        style={({ pressed }) => [
          styles.cancelBtn,
          pressed && { opacity: 0.85 },
          loading && { opacity: 0.6 },
        ]}
        accessibilityRole="button"
        accessibilityLabel={cancelLabel}
      >
        <MaterialIcons
          name="close"
          size={16}
          color={fpColors.onSurface}
        />
        <Text style={styles.cancelText}>{cancelLabel}</Text>
      </Pressable>
      <Pressable
        onPress={onApply}
        disabled={loading || disabled}
        style={({ pressed }) => [
          styles.applyBtn,
          pressed && { opacity: 0.9 },
          (loading || disabled) && { opacity: 0.6 },
        ]}
        accessibilityRole="button"
        accessibilityLabel={applyLabel}
      >
        {loading ? (
          <ActivityIndicator size="small" color={fpColors.onPrimary} />
        ) : (
          <MaterialIcons
            name="check-circle"
            size={16}
            color={fpColors.onPrimary}
          />
        )}
        <Text style={styles.applyText}>{applyLabel}</Text>
      </Pressable>
    </View>
  );

  // Portal to document.body. Using `ReactDOM.createPortal` keeps the
  // bar outside the stage's clipping context.
  if (typeof document === 'undefined') return bar;
  return ReactDOM.createPortal(bar, document.body);
};

const styles = StyleSheet.create({
  bar: {
    position: 'fixed' as any,
    bottom: 24,
    left: '50%',
    transform: [{ translateX: -160 }],
    width: 320,
    flexDirection: 'row',
    gap: 10,
    padding: 8,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 10px 30px rgba(31,26,34,0.25)',
          zIndex: 9999,
        } as any)
      : null),
  },
  cancelBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  cancelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  applyBtn: {
    flex: 1.3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 999,
    backgroundColor: fpColors.primary,
  },
  applyText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
});
